import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wordmark, Footer, Shield } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success("Signed in.");
      if (user.role === "operator") navigate("/operator");
      else if (!user.onboarding_complete) navigate("/intake");
      else navigate("/app/today");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "If the account exists, a reset process has been started.");
      setForgot(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-wr-bg flex flex-col wr-grid-bg">
      <header className="border-b border-wr-border bg-black/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Wordmark size="md" />
          <Link to="/" className="wr-label hover:text-white flex items-center gap-2" data-testid="login-back-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-wr-surface border border-wr-border p-7 sm:p-9 wr-fade-up">
          <Shield className="h-12 w-12 mb-4" />
          <p className="wr-label !text-wr-red">▸ SARGE · SECURE ACCESS</p>
          <h1 className="wr-heading font-bold text-white text-2xl mt-3">
            {forgot ? "Reset access" : "Sign in to WarRoom"}
          </h1>
          <p className="text-sm text-wr-sub mt-2">
            {forgot
              ? "Enter your email and we'll send a secure link to reset your password."
              : "Sign in to your Founding WarRoom."}
          </p>

          {!forgot ? (
            <form onSubmit={submit} className="mt-7 space-y-5" data-testid="login-form">
              <div>
                <label className="wr-label">Email</label>
                <Input
                  required
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                />
              </div>
              <div>
                <label className="wr-label">Password</label>
                <Input
                  required
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                />
              </div>
              {error && (
                <p className="text-sm text-wr-red wr-mono" data-testid="login-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11"
              >
                {busy ? "Verifying…" : "Sign In"}
              </Button>
              <button
                type="button"
                onClick={() => setForgot(true)}
                data-testid="login-forgot"
                className="wr-label hover:text-white block"
              >
                Forgot password?
              </button>
            </form>
          ) : (
            <form onSubmit={submitForgot} className="mt-7 space-y-5" data-testid="forgot-form">
              <div>
                <label className="wr-label">Email</label>
                <Input
                  required
                  type="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <button
                type="button"
                onClick={() => setForgot(false)}
                data-testid="forgot-cancel"
                className="wr-label hover:text-white block"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
